'use client';

import type { ComponentProps, ReactNode } from 'react';
import { Sheet, SheetContent, SheetHeader } from '@/components/ui/sheet';
import { isUnattributedOutsideInteraction } from '@/components/attributes';
import { cn } from '@/lib/utils';

/**
 * **The slide-over width scale — the smallest set of sizes that works** (VALE-863).
 *
 * Before this, every record slide-over picked its own width at its own call
 * site, and the two that a user moves between most read as different products:
 * the Contacts drawer was capped at `sm:max-w-2xl` (**672px**) while the
 * Inventory drawer ran `sm:w-[57%]` (**~860px** on a 1512px viewport). Tom's
 * 27 Aug review named exactly that gap — *"materially narrower than the Asset
 * slide-over with no apparent reason"* — and it had none: the cap was the
 * shell's own default from before Inventory measured what it needed.
 *
 * So the widths are named here rather than restated per caller, and the two
 * record surfaces share one. A caller asks for the *kind* of surface it is; it
 * does not type a percentage.
 *
 * | Size      | Width          | For                                                    |
 * | --------- | -------------- | ------------------------------------------------------ |
 * | `record`  | `sm:w-[57%]`   | A record's fields — Contacts, Inventory                |
 * | `half`    | `sm:w-1/2`     | The shell's historical default; anything not yet sized |
 *
 * **`record` is 57%, not the old 672px cap, deliberately.** The number is
 * Inventory's, and it was measured rather than chosen (VALE-294): the attribute
 * grid's label column is a container query — `22.5cqw` at two columns — so the
 * surface's own width decides whether a label fits, and at `sm:w-1/2` the
 * computed label lost to the clamp floor and ellipsised 112 labels across the
 * 22 asset types. Harmonising *up* therefore costs Contacts nothing (its
 * vocabulary is shorter — ~18 characters against Inventory's ~25, so it already
 * fit) and buys it the room the same review asked for elsewhere on the form,
 * where the Address value was being squeezed onto three lines.
 *
 * **Workflow panels that host document editing are out of scope and stay their
 * own size** — Discovery's owner-search hubs (`w-3/4 max-w-[1400px]`) and the
 * Redemption sheets are not record surfaces, and the review says so explicitly.
 * They pass their own `className` and never a `size`.
 */
export const DRAWER_SIZE = {
  record: 'sm:w-[57%] sm:max-w-none',
  half: 'sm:w-1/2 sm:max-w-none'
} as const;

export type DrawerSize = keyof typeof DRAWER_SIZE;

export interface SingleItemEditDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rendered inside the fixed `SheetHeader` — callers own their own title/icon/badge layout. */
  header: ReactNode;
  /** Scrollable body content. */
  children: ReactNode;
  /**
   * Receives the non-scrolling footer's DOM node once mounted, so a caller's
   * form card can portal its Save/Cancel/Delete row into it (see
   * `InventoryFormCard`/`ContactFormCard`'s `actionsContainer` prop). A plain
   * sibling after the scrollable body — not `position: sticky` — matching
   * the redemption sheets' established convention. Empty and zero-height
   * when nothing is portaled into it.
   *
   * **Optional, because a footerless surface exists** (VALE-75 / [INVDRAW-4]).
   * On the attribute treatment nothing is portaled here at all — Delete moved
   * into the header and there is no Save — so requiring the callback made a
   * caller take a node it had no use for, and the only way to satisfy it was a
   * no-op that read as an oversight. Omitting it is now how a caller says *"this
   * surface has no footer"*; the container itself still renders, and stays the
   * zero-height div it already is when nothing fills it.
   */
  onFooterContainerChange?: (node: HTMLDivElement | null) => void;
  /**
   * Which width from {@link DRAWER_SIZE} this surface takes. Defaults to
   * `half`, the shell's historical width, so every caller that predates the
   * scale renders byte-for-byte as it did.
   *
   * Prefer this over typing a width into `className` — that is how the
   * Contacts/Inventory divergence VALE-863 fixed got in.
   */
  size?: DrawerSize;
  /**
   * Extra classes on the sheet panel. Use `size` for width; this is for
   * everything else (the attribute drawers' close-button offset) and for the
   * workflow panels that are deliberately outside the scale.
   */
  className?: string;
  /**
   * Lets a caller keep `Escape` for itself. Radix's dismissable layer owns that
   * key, so on a commit-on-blur surface one `Escape` would both revert the open
   * field **and** dismiss the whole sheet — `preventDefault()` here stops the
   * second half. Mirrors `lead-drawer`'s guard on the enquiry surface.
   *
   * Optional and unwired by default, so `SimpleInventoryDrawer` is untouched.
   */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  /**
   * Lets a caller intercept an outside click **before** Radix's dismissable
   * layer decides whether to close the sheet (VALE-60).
   *
   * Radix detects an outside click on `pointerdown`, which fires *before* the
   * browser's own mousedown-driven focus change — so on a commit-on-blur
   * surface, clicking outside can close the sheet before the open field's
   * `blur` handler ever runs, silently discarding whatever was typed. Passing
   * a handler here runs inside that same `pointerdown` dispatch, in time to
   * force the commit through before the dismiss proceeds.
   *
   * Optional and unwired by default, so `SimpleInventoryDrawer` is untouched.
   */
  onPointerDownOutside?: ComponentProps<
    typeof SheetContent
  >['onPointerDownOutside'];
  /**
   * Lets a caller take the outside-interaction decision away from the shell's
   * own default (VALE-1677) — passed straight through to `SheetContent`, ahead
   * of {@link isUnattributedOutsideInteraction}. See this component's own
   * `onInteractOutside` wiring for why that default exists and why it is
   * unconditional rather than opt-in.
   */
  onInteractOutside?: ComponentProps<typeof SheetContent>['onInteractOutside'];
  /**
   * Takes initial focus on the drawer container rather than the first tabbable
   * element inside it (LF-6307 / [ATTR-6]).
   *
   * Radix focuses the first tabbable child on open. On a form that is the first
   * input, which is right — but on a **read-out** surface with no input boxes
   * at rest it is whatever sits in the header, and once the Contacts drawer
   * mounted its destructive action there that became a focused button reading
   * `Delete contact`, inviting the Enter key. Normal `Tab` order is unaffected;
   * only the initial landing moves.
   *
   * **Opt-in, and it must stay that way.** `SimpleInventoryDrawer` is a real
   * form whose first tabbable element *is* its first input, so focusing it on
   * open is correct there — making this unconditional would regress a surface
   * that is fine. Default `false`; only the Contacts attribute path opts in.
   *
   * @default false
   */
  focusContainerOnOpen?: boolean;
}

/**
 * Shared shell for a single-item, edit-first drawer with a pinned action
 * footer — the layout `SimpleInventoryDrawer` (LF-5542) and
 * `ContactDetailsDrawer` (LF-5547) both need. Extracted after the two
 * independently drifted: `SimpleInventoryDrawer` zeroed the `SheetContent`
 * padding and owned it in the body/footer divs, `ContactDetailsDrawer` kept
 * the default padding, so its portaled footer inherited an extra ~20px
 * inset the inventory drawer didn't have.
 *
 * The fix is structural, not a magic number: `SheetContent` is **always**
 * `p-0` here — every caller owns 100% of their own spacing in `header` /
 * `children` / whatever they portal into the footer, so there's no shared
 * default padding left to drift out of sync between callers.
 */
export function SingleItemEditDrawer({
  open,
  onOpenChange,
  header,
  children,
  onFooterContainerChange,
  size = 'half',
  className,
  onEscapeKeyDown,
  onPointerDownOutside,
  onInteractOutside,
  focusContainerOnOpen = false
}: SingleItemEditDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        aria-describedby={undefined}
        onEscapeKeyDown={onEscapeKeyDown}
        onPointerDownOutside={onPointerDownOutside}
        /*
          **Refuse an outside-interaction verdict reached with no attribution**
          (VALE-1677, extending VALE-1652 off `ai-import-dialog-content.tsx`).
          Every caller of this shell can host the attribute treatment's
          composite popovers, whose capture-phase commit can unmount the
          pointerdown target before Radix's own dismissable layer looks at it —
          see `isUnattributedOutsideInteraction`'s own doc for the mechanism.
          Baked in here rather than left to each caller, per
          `components/attributes/CLAUDE.md` §6d: "the surface nobody
          reproduces against cannot be the one left broken." The caller runs
          first, so a mount can still take the decision away from the shell.
        */
        onInteractOutside={(event) => {
          onInteractOutside?.(event);
          if (event.defaultPrevented) return;
          if (isUnattributedOutsideInteraction(event)) event.preventDefault();
        }}
        /*
          Radix's `FocusScope` renders the content with `tabIndex={-1}`, so the
          container is programmatically focusable — focusing it explicitly is
          what keeps the focus trap and the screen-reader announcement intact.
          `preventDefault()` alone would leave focus on the trigger, outside
          the sheet.
        */
        onOpenAutoFocus={
          focusContainerOnOpen
            ? (event) => {
                event.preventDefault();
                (event.currentTarget as HTMLElement | null)?.focus();
              }
            : undefined
        }
        className={cn(
          'flex w-full flex-col overflow-hidden p-0',
          DRAWER_SIZE[size],
          className
        )}
      >
        <SheetHeader className="flex-shrink-0 space-y-4 p-6 pt-8">
          {header}
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 pb-6">
          {children}
        </div>

        <div ref={onFooterContainerChange} />
      </SheetContent>
    </Sheet>
  );
}
